import React from 'react';
import { useField, useFormikContext, ErrorMessage } from 'formik';
import classNames from 'classnames';
import Dropdown from './Dropdown';
import { FormFieldTooltip } from './FormFieldTooltip';

import toPath from 'lodash-es/toPath';

export const deepGet = (obj: any, path: string) => {
    const segments = toPath(path);
    let val = obj;
    for (const segment of segments) {
        if (val === undefined) return val;
        val = val[segment];
    }
    return val;
};

interface FormSingleSelectFieldProps {
    fieldName: string;
    label: string;
    options: { value: string | number | null; label: string }[];
    readOnly?: boolean;
    required?: boolean;
    dropdownProps?: object;
    tooltipIconData?: { mainIcon: string; closeIcon: string };
    tooltip?: string;
    renderTooltipTextAsHtml?: boolean;
    customClassName?: string;
}

const FormSingleSelectField: React.FC<FormSingleSelectFieldProps> = ({
    fieldName,
    label,
    options,
    readOnly,
    required,
    dropdownProps,
    tooltipIconData,
    tooltip,
    renderTooltipTextAsHtml,
    customClassName,
}) => {
    const formik = useFormikContext();
    const validate = required
        ? (value: any) => (value == null || value === '' ? `${label} is required.` : undefined)
        : undefined;

    const [_, meta] = useField({ name: fieldName, validate });
    const hasError = !!(meta.touched && meta.error);

    const handleChange = (e: { value: string }) => {
        formik.setFieldValue(fieldName, e.value);
    };

    return (
        <div
            className={classNames(
                `${
                    customClassName
                        ? customClassName + ' FormBox FormSideBySide'
                        : 'FormBox FormSideBySide'
                }`,
                {
                    FormFieldRequired: required,
                }
            )}
        >
            <div className="FormLabel">
                <label htmlFor={fieldName}>{label}</label>
                {tooltip && tooltipIconData && (
                    <FormFieldTooltip
                        tooltipText={tooltip}
                        tooltipMainIconSource={tooltipIconData.mainIcon}
                        tooltipCloseIconSource={tooltipIconData.closeIcon}
                        renderTooltipTextAsHtml={renderTooltipTextAsHtml}
                    />
                )}
            </div>
            <div className="FormField">
                <Dropdown
                    id={fieldName}
                    onChange={handleChange}
                    options={options}
                    value={options.find((o) => o.value === deepGet(formik.values, fieldName))}
                    readOnly={readOnly}
                    roundedRectangle={true}
                    dropdownProps={dropdownProps}
                    hasError={hasError}
                />
                <ErrorMessage name={fieldName} />
            </div>
        </div>
    );
};

export default FormSingleSelectField;
